import classNames from "classnames";
import { ButtonHTMLAttributes, ForwardedRef, forwardRef } from "react";

type Variants =
  | "primary-black"
  | "primary-danger"
  | "secondary"
  | "tertiary"
  | "outline"
  | "discussion";

type Sizes = "big" | "sm" | "xs";

type IconPositioning = "leading" | "trailing" | "only";

type GeneralButtonTypes = {
  variant?: Variants;
  size?: Sizes;
  icon?: IconPositioning;
};

type CustomButtonTypes<V extends Variants> = V extends "tertiary"
  ? { variant: V; size?: never; icon?: IconPositioning }
  : V extends "discussion"
  ? {
      variant: V;
      size?: never;
      icon: Extract<IconPositioning, "leading" | "only">;
    }
  : {
      variant?: V;
      size?: Sizes;
      icon?: IconPositioning;
    };

export type ForumButtonProps = CustomButtonTypes<Variants>;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  ForumButtonProps;

const secondaryClasses =
  "bg-gray-7 text-gray-1 border-transparent [&:not(:disabled)]:hover:bg-gray-8 focus-visible:bg-gray-8 [&:not(:disabled)]:active:text-gray-1 [&:not(:disabled)]:active:border-gray-1 [&:not(:disabled)]:active:bg-gray-8";

const variantClasses = {
  ["primary-black"]:
    "bg-gray-1 text-white border-gray-1 [&:not(:disabled)]:hover:bg-gray-2 [&:not(:disabled)]:hover:border-gray-2 focus-visible:bg-gray-2 focus-visible:border-gray-2 [&:not(:disabled)]:active:bg-gray-1 [&:not(:disabled)]:active:border-gray-1",
  ["primary-danger"]:
    "bg-danger-2 text-white border-danger-2 [&:not(:disabled)]:hover:bg-danger-1 [&:not(:disabled)]:hover:border-danger-1 [&:not(:disabled)]:active:bg-danger-1 [&:not(:disabled)]:active:border-danger-1 focus-visible:bg-danger-1 focus-visible:border-danger-1 focus-visible:shadow-elevation-1",
  secondary: secondaryClasses,
  tertiary:
    "border-transparent bg-transparent text-gray-3 [&:not(:disabled)]:hover:bg-gray-8 [&:not(:disabled)]:hover:text-gray-2 focus-visible:bg-gray-8 focus-visible:text-gray-2 [&:not(:disabled)]:active:bg-gray-8 [&:not(:disabled)]:active:text-gray-1 disabled:bg-transparent disabled:border-transparent",
  outline: "border-gray-5 bg-transparent text-gray-3 [&:not(:disabled)]:hover:bg-gray-8 [&:not(:disabled)]:hover:text-gray-2 focus-visible:bg-gray-8 focus-visible:text-gray-2 [&:not(:disabled)]:active:bg-gray-8 [&:not(:disabled)]:active:text-gray-1 disabled:bg-transparent disabled:border-gray-8",
  discussion: `${secondaryClasses} [&:not(:disabled)]:hover:text-gray-2 focus-visible:text-gray-2`,
};

const subVariantClasses = {
  big: "h-10 px-4",
  "big-leading": "h-10 pl-2 pr-3 gap-2",
  "big-trailing": "h-10 pl-3 pr-2 gap-2",
  "big-only": "h-10 w-10",
  sm: "h-8 px-4",
  "sm-leading": "h-8 pl-2 pr-3 gap-1.5",
  "sm-trailing": "h-8 pl-3 pr-2 gap-1.5",
  "sm-only": "h-8 w-8",
  xs: "h-6 px-1",
  "xs-leading": "h-6 pl-1 pr-1 gap-1",
  "xs-trailing": "h-6 pl-1 pr-1 gap-1",
  "xs-only": "h-6 w-5",
  tertiary: "h-8 px-2",
  "tertiary-leading": "h-8 pl-1.5 pr-2 gap-1",
  "tertiary-trailing": "h-8 pl-2 pr-1.5 gap-1",
  "tertiary-only": "h-8 w-8",
  outline: "h-8 px-2",
  "outline-leading": "h-8 pl-1.5 pr-2 gap-1",
  "outline-trailing": "h-8 pl-2 pr-1.5 gap-1",
  "outline-only": "h-8 w-8",
  "discussion-leading": "h-8 pl-3 pr-4 gap-1.5",
  "discussion-only": "h-8 w-8",

};

export const getButtonClassName = ({
  variant = "primary-black",
  size = "sm",
  icon,
}: GeneralButtonTypes = {}) => {
  let subVariantClass;

  switch (variant) {
    case "discussion":
      switch (icon) {
        case "leading":
        case "only":
          subVariantClass = subVariantClasses[`${variant}-${icon}`];
      }
      break;
    case "tertiary":
    case "outline":
      if (icon) {
        subVariantClass = subVariantClasses[`${variant}-${icon}`];
      } else {
        subVariantClass = subVariantClasses[variant];
      }
      break;
    default:
      if (icon) {
        subVariantClass = subVariantClasses[`${size}-${icon}`];
      } else {
        subVariantClass = subVariantClasses[size];
      }
  }

  return classNames(
    "rounded-md transition font-style-label-sm border flex items-center justify-center disabled:cursor-not-allowed disabled:bg-white disabled:text-gray-5 disabled:border-gray-6",
    variantClasses[variant],
    subVariantClass
  );
};

export const Button = forwardRef(function Button(
  {
    children,
    variant = "primary-black",
    size = "sm",
    icon,
    className = "",
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  return (
    <button
      className={classNames(
        getButtonClassName({ variant, size, icon }),
        className
      )}
      type="button"
      ref={ref}
      {...props}
    >
      {children}
    </button>
  );
});
